import { Dec, type Decimal } from '../shared/decimal'
import { UPGRADE_FAMILIES } from './constants'

// Canonical copies of the formulas the pristine code duplicates (step 5,
// Tier 1 — verified byte-identical or cleanly parameterizable). The
// duplicate sites in site/scripts are redirected here via the __engine
// global; computeDerived and step-11's tickLarge consume the same functions.
// Formulas whose copies DIVERGE (magifold effect ladder, dragonTimeEffect
// caps, the clamp-less knowledgeUpgrade2Effect display copy, the orange-sigil
// exponent mismatch) are NOT here — they are documented quirks, decided later.

// game stays untyped until step 6's GameState.
type GameState = Record<string, any>

/** ×4 (sigils.js, magicChallenges.js, dragon.js, script.js updateSmall). */
export function magifolds(game: GameState): Decimal {
  if (game.inHell) return new Dec(1)
  return game.magicScore1.add(1)
    .mul(game.magicScore2.add(1))
    .mul(game.magicScore3.add(1))
    .mul(game.magicScore4.add(1))
}

/**
 * The CAP COMPUTATION duplicated ×3 (magic.js:69-70/:227-228,
 * script.js:2245-2246). The divergent application halves (clamping a local
 * vs clamping game.magic, innerHTML vs textContent) stay at their sites.
 */
export function magicHardcapValue(game: GameState): Decimal {
  let cap: Decimal = new Dec('e5000000')
  if (game.tomeUpgradesBought[8] == true) {
    cap = cap.pow(game.blueFireUpgradesBought[3].pow(0.7).mul(5).add(1))
  }
  return cap
}

/** The miner price ratio, duplicated ×2 inside the buy-max series calls. */
export function minerCostBase(game: GameState): Decimal {
  return new Dec(1.1).sub(game.platinumUpgradesBought[3] * 0.005)
}

/** ×4 (gold.js:10/:24, alchemy.js:50/:116). */
export function minerCost(game: GameState): Decimal {
  return minerCostBase(game).pow(game.miners).mul(20).floor()
}

export interface KnowledgeRewardOpts {
  /** 1-based trade level driving the 1.5^ (level-1) base. */
  level: Decimal
  /** The per-trade Multipliers[3] factor (randomize/set sites); omit for updateLarge's accrual. */
  tradeMultiplier?: Decimal
  /** updateLarge guards the darkEssence term on ...UpgradesBought[2].gt(0); the trade sites use bare unlocks>=33. */
  strictDarkEssenceGuard?: boolean
  /** updateLarge halves before flooring (.div(2).floor()). */
  halved?: boolean
}

/**
 * The FULL 10-step reward chain, ×3 (knowledge.js:13-22 byte-identical to
 * :35-44; script.js:2285-2294 with its three exact differences reproduced by
 * the opts).
 */
export function knowledgeTradeReward(game: GameState, opts: KnowledgeRewardOpts): Decimal {
  let r: Decimal = new Dec(1.5).pow(opts.level.sub(1))
  if (opts.tradeMultiplier !== undefined) r = r.mul(opts.tradeMultiplier)
  r = r.mul(new Dec(2).pow(game.knowledgeUpgradesBought[0].pow(0.5)))
  if (game.tomeUpgradesBought[2] == true) r = r.mul(2)
  if (game.tomeUpgradesBought[6] == true) r = r.mul(game.totalTomes.pow(1.2).add(1))
  if (game.tomeUpgradesBought[8] == true) r = r.mul(new Dec(1000).pow(game.blueFireUpgradesBought[5].pow(0.6)))
  if (game.holyTetrahedronUpgradesBought[7]) r = r.mul(1e150)
  if (game.holyOctahedronUpgradesBought[8]) r = r.mul('e550')
  if (game.voidMagicUpgradesBought[11] == true) r = r.mul('e12000')
  const darkGuard = opts.strictDarkEssenceGuard
    ? game.unlocks >= 33 && game.darkEssenceUpgradesBought[2].gt(0)
    : game.unlocks >= 33
  if (darkGuard) r = r.mul(new Dec(10).pow(new Dec(2).pow(game.darkEssenceUpgradesBought[2].pow(0.5)).mul(5e9)))
  if (opts.halved) return r.div(2).floor()
  return r.floor()
}

/**
 * The TRUNCATED 5-step preview chain, ×2 (knowledge.js:83-87 byte-identical
 * to script.js:861-865). Faithfully truncated: no trade multiplier, stops at
 * the tome-8 term, and the floor is FUSED into the tome-8 branch — an
 * un-floored value is returned when that upgrade isn't owned. QUIRK, kept.
 */
export function knowledgeTradeRewardPreview(game: GameState, level: Decimal): Decimal {
  let r: Decimal = new Dec(1.5).pow(level.sub(1))
  r = r.mul(new Dec(2).pow(game.knowledgeUpgradesBought[0].pow(0.5)))
  if (game.tomeUpgradesBought[2] == true) r = r.mul(2)
  if (game.tomeUpgradesBought[6] == true) r = r.mul(game.totalTomes.pow(1.2).add(1))
  if (game.tomeUpgradesBought[8] == true) r = r.mul(new Dec(1000).pow(game.blueFireUpgradesBought[5].pow(0.6))).floor()
  return r
}

/**
 * ×4 identical clamped copies (knowledge.js:119-121/:151-153,
 * script.js:851-853/:1586-1588). The clamp-less sigils.js copy is a
 * documented display quirk and is NOT redirected here.
 */
export function knowledgeUpgrade2EffectValue(game: GameState): Decimal {
  let e: Decimal = new Dec(5).pow(game.knowledgeUpgradesBought[1].pow(0.5))
  if (e.gt(1e20)) e = e.mul(1e60).pow(0.25)
  if (e.gt('e2e7')) e = new Dec('e2e7')
  return e
}

/**
 * The generic upgrade cost shape shared by every {base, initialCost} family
 * (fire.js:64 and ~30 buy/max pairs — E2). Site redirects land with each
 * system's step-9 rewrite; the function + table ship now.
 */
export function upgradeCost(
  family: keyof typeof UPGRADE_FAMILIES, index: number, bought: Decimal
): Decimal {
  const cfg = UPGRADE_FAMILIES[family]
  return new Dec(cfg.base[index] ?? 0).pow(bought).mul(cfg.initialCost[index] ?? 0).floor()
}
